// StreakModal — детали стрика. Desktop: Dialog. Mobile: Sheet (bottom).
// Big number + heatmap + freezes CTA + settings CTA + milestone confetti hook (visual only).

import { Badge } from "@studyhub/ui/components/badge";
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from "@studyhub/ui/components/bottom-sheet";
import { Button } from "@studyhub/ui/components/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@studyhub/ui/components/dialog";
import { Progress } from "@studyhub/ui/components/progress";
import { ScrollArea } from "@studyhub/ui/components/scroll-area";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import {
	IconAlertTriangle,
	IconFlame,
	IconSettings,
	IconSnowflake,
	IconSparkles,
	IconTarget,
	IconTrophy,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CountUp } from "@/components/shared/count-up";
import { useRealtime } from "@/hooks/use-realtime";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { useIsLgUp } from "@/lib/platform-detect";
import { type StreakState, useStreakState, useUseFreeze } from "./_api";
import { StreakHeatmap } from "./streak-heatmap";

// Стабильная ссылка для useRealtime invalidation.
const STREAK_REALTIME_KEYS: ReadonlyArray<ReadonlyArray<unknown>> = [["streaks", "state"]];

interface Props {
	open: boolean;
	onClose: () => void;
	/** Demo state для visual showcase (?demo=streak-broken|milestone|...). */
	demo?: string;
}

function nextMilestone(current: number): number {
	const stops = [7, 14, 30, 60, 100, 180, 365];
	return stops.find((s) => s > current) ?? current + 30;
}

function MilestoneBurst(): React.ReactElement {
	// Лёгкая «confetti»-аналогия: несколько искорок Tabler с пульсацией.
	return (
		<div
			aria-hidden
			className="pointer-events-none absolute inset-x-0 top-2 flex justify-center gap-2 motion-reduce:hidden"
		>
			{[0, 1, 2, 3, 4].map((i) => (
				<IconSparkles
					key={i}
					size={14}
					className="text-warning animate-pulse"
					style={{ animationDelay: `${i * 80}ms` }}
				/>
			))}
		</div>
	);
}

function StreakBody({
	state,
	onClose,
}: {
	state: StreakState;
	onClose: () => void;
}): React.ReactElement {
	const { t } = useTranslation("streaks");
	const useFreeze = useUseFreeze();
	const isDesktop = useIsLgUp();
	const commerceVisible = useCommerceVisible();
	const [showAbout, setShowAbout] = useState(false);
	const milestone = nextMilestone(state.currentStreak);
	const progressToMilestone = Math.min(100, (state.currentStreak / milestone) * 100);
	const isBroken = state.currentStreak === 0;
	const isMilestoneHit = state.milestoneJustHit != null;
	const atRisk = state.atRisk;

	return (
		<div className="space-y-0">
			{/* Big number header */}
			<div className="relative flex flex-col items-center py-6 px-6">
				{isMilestoneHit && <MilestoneBurst />}
				<IconFlame
					size={isDesktop ? 48 : 40}
					className={isBroken ? "text-muted-foreground" : "text-warning"}
					stroke={1.5}
				/>
				<div className="text-5xl lg:text-6xl font-bold tabular-nums leading-none mt-2">
					<CountUp value={state.currentStreak} />
				</div>
				<div className="text-sm text-muted-foreground mt-1">
					{t("current", { days: state.currentStreak })} {t("currentSuffix")}
				</div>
				{!isBroken && (
					<div className="mt-4 w-full max-w-sm">
						<div className="flex items-baseline justify-between text-2xs text-muted-foreground mb-1 tabular-nums">
							<span>{t("nextMilestone", { days: milestone - state.currentStreak })}</span>
							<span>{Math.round(progressToMilestone)}%</span>
						</div>
						<Progress value={progressToMilestone} className="h-2 [&>div]:bg-warning" />
					</div>
				)}
			</div>

			{/* At-risk banner */}
			{atRisk && (
				<div className="mx-6 mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
					<IconAlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
					<div>
						<div className="font-medium text-warning">{t("states.atRiskTitle")}</div>
						<div className="text-muted-foreground mt-0.5">{t("states.atRiskBody")}</div>
					</div>
				</div>
			)}

			{/* Broken banner */}
			{isBroken && (
				<div className="mx-6 mb-3 rounded-lg border border-border bg-muted/30 px-3 py-3 text-center">
					<div className="text-sm font-medium">{t("states.brokenTitle")}</div>
					<div className="text-xs text-muted-foreground mt-1">
						{t("states.brokenBody", { days: state.longestStreak })}
					</div>
				</div>
			)}

			{/* 3 mini stats */}
			<div className="grid grid-cols-3 gap-3 px-6 py-4 border-y border-border">
				<div className="rounded-md border border-border bg-muted/20 p-3 min-h-[80px]">
					<div className="flex min-w-0 items-center gap-1 text-2xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
						<IconFlame size={12} className="text-warning" />
						{t("current", { days: state.currentStreak })}
					</div>
					<div className="text-md font-semibold tabular-nums">{state.currentStreak}</div>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3 min-h-[80px]">
					<div className="flex min-w-0 items-center gap-1 text-2xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
						<IconTrophy size={12} className="text-success" />
						{t("longest")}
					</div>
					<div className="text-md font-semibold tabular-nums">{state.longestStreak}</div>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3 min-h-[80px]">
					<div className="flex min-w-0 items-center gap-1 text-2xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
						<IconTarget size={12} className="text-info" />
						{t("dailyGoal")}
					</div>
					<div className="text-md font-semibold tabular-nums">
						{t("dailyGoalValue", { minutes: state.dailyGoalMinutes })}
					</div>
				</div>
			</div>

			{/* Freezes — честные данные из реального freezeBalance (никаких
			    фабрикованных «manual N/limit»; backend useFreeze тратит общий запас). */}
			<div className="px-6 py-4 space-y-2 border-b border-border">
				<div className="flex items-center justify-between gap-2">
					<h4 className="text-sm font-semibold flex items-center gap-1.5">
						<IconSnowflake size={14} className="text-info" />
						{t("freezes.title")}
					</h4>
					<Badge variant="info" className="gap-1 tabular-nums">
						<IconSnowflake size={12} />
						{state.freezesRemaining}
					</Badge>
				</div>
				<div className="text-xs text-muted-foreground tabular-nums">
					{state.freezesRemaining > 0
						? t("freezes.balance", { count: state.freezesRemaining })
						: t("freezes.none")}
				</div>
				{state.freezesUsedThisWeek > 0 && (
					<div className="text-xs text-muted-foreground tabular-nums">
						{t("freezes.usedThisWeek", { count: state.freezesUsedThisWeek })}
					</div>
				)}
				{showAbout && (
					<p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
						{t("freezes.aboutText")}
					</p>
				)}
				<div className="flex flex-wrap gap-2 pt-1">
					<Button
						variant="outline"
						size="sm"
						className="cursor-pointer min-h-[44px] sm:min-h-0"
						disabled={state.freezesRemaining === 0 || useFreeze.isPending}
						onClick={() => useFreeze.mutate()}
					>
						<IconSnowflake size={14} />
						{t("freezes.useManual")}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="cursor-pointer min-h-[44px] sm:min-h-0"
						aria-expanded={showAbout}
						onClick={() => setShowAbout((v) => !v)}
					>
						{t("freezes.about")}
					</Button>
				</div>
			</div>

			{/* Heatmap */}
			<div className="px-6 py-4 space-y-3 border-b border-border">
				<h4 className="text-sm font-semibold">{t("heatmap.title")}</h4>
				<StreakHeatmap history={state.history} cols={isDesktop ? 10 : 7} />
			</div>

			{/* Settings */}
			<div className="px-6 py-4 space-y-1.5">
				<div className="flex items-center justify-between gap-3 text-xs">
					<span className="shrink-0 text-muted-foreground">{t("settings.timezone")}</span>
					<span className="min-w-0 break-all text-right tabular-nums">{state.timezone}</span>
				</div>
				<div className="flex items-center justify-between text-xs">
					<span className="text-muted-foreground">{t("settings.reminder")}</span>
					<span className="tabular-nums">{String(state.reminderHour).padStart(2, "0")}:00</span>
				</div>
				<div className="pt-2">
					<Button asChild variant="link" size="sm" className="px-0 cursor-pointer">
						<Link to="/settings/streak" onClick={onClose}>
							<IconSettings size={14} />
							{t("settings.edit")}
						</Link>
					</Button>
				</div>
			</div>

			{/*
				Бейдж уровня. Скрыт вместе с продажей: App Store 3.1.1 требует, чтобы
				подписки не было видно, пока её нельзя купить через IAP, а «Plus / Pro»
				— это её название. См. `lib/module-availability/commerce`.
			*/}
			{state.tier === "free" && commerceVisible && (
				<div className="px-6 pb-4">
					<Badge variant="info">Plus / Pro</Badge>
				</div>
			)}
		</div>
	);
}

function LoadingBody(): React.ReactElement {
	return (
		<div className="space-y-4 p-6">
			<div className="flex flex-col items-center gap-3">
				<Skeleton className="size-12 rounded-full" />
				<Skeleton className="h-12 w-32" />
				<Skeleton className="h-4 w-40" />
			</div>
			<Skeleton className="h-24 w-full" />
			<Skeleton className="h-32 w-full" />
		</div>
	);
}

export function StreakModal({ open, onClose, demo }: Props): React.ReactElement {
	const isDesktop = useIsLgUp();
	const { t } = useTranslation("streaks");
	const query = useStreakState({ demo, enabled: open });
	const [showConfetti, setShowConfetti] = useState(false);

	// Real-time milestone — invalidate streak state и триггерим confetti.
	// Подписка на Socket.IO `streaks:milestone` (Phase 5 — Streaks модуль).
	useRealtime({
		channel: "streaks:milestone",
		invalidateQueryKeys: STREAK_REALTIME_KEYS,
		onEvent: () => setShowConfetti(true),
	});

	useEffect(() => {
		if (query.data?.milestoneJustHit && open) {
			setShowConfetti(true);
			const tm = setTimeout(() => setShowConfetti(false), 1500);
			return () => clearTimeout(tm);
		}
		return undefined;
	}, [query.data?.milestoneJustHit, open]);

	// Подавление tabs-confetti, чтобы не показывалось каждый раз при open. Visual-only.
	void showConfetti;

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
				{/* h-[85dvh] (НЕ max-h): фикс. высота даёт flex-1 ScrollArea определённый
				    базис → контент скроллится (контента всегда много: число + статы +
				    заморозки + heatmap + настройки). Канон §7.2. Закрытие — встроенный
				    крестик DialogContent + клик по подложке (нижнюю «Закрыть» убрали). */}
				<DialogContent className="flex h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
					<DialogHeader className="shrink-0 border-b border-border px-6 py-4">
						<DialogTitle className="flex items-center gap-2">
							<IconFlame size={18} className="text-warning" />
							{t("title")}
						</DialogTitle>
					</DialogHeader>
					<ScrollArea className="h-full min-h-0 flex-1">
						{query.isLoading || !query.data ? (
							<LoadingBody />
						) : (
							<StreakBody state={query.data} onClose={onClose} />
						)}
					</ScrollArea>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<BottomSheet open={open} onOpenChange={(o) => !o && onClose()}>
			<BottomSheetContent className="flex h-[92dvh] flex-col gap-0 overflow-hidden p-0">
				<div className="shrink-0 border-b border-border px-6 pb-3 pt-1 text-left">
					<BottomSheetTitle className="flex items-center gap-2 text-base">
						<IconFlame size={18} className="text-warning" />
						{t("title")}
					</BottomSheetTitle>
				</div>
				<ScrollArea className="h-full min-h-0 flex-1">
					{query.isLoading || !query.data ? (
						<LoadingBody />
					) : (
						<StreakBody state={query.data} onClose={onClose} />
					)}
				</ScrollArea>
				{/* Только функциональное действие — закрытие свайпом вниз / тапом по
				    подложке (дубль-кнопку «Закрыть» убрали). */}
				<div
					className="flex shrink-0 border-t border-border bg-background px-4 pt-3"
					style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
				>
					<Button asChild className="h-11 flex-1 cursor-pointer">
						<Link to="/settings/streak" onClick={onClose}>
							<IconSettings size={14} />
							{t("settings.open")}
						</Link>
					</Button>
				</div>
			</BottomSheetContent>
		</BottomSheet>
	);
}
